import React, { useContext, useEffect, useState } from "react";
import {ButtonGroup, Stack, Grid, Box, Typography, Chip} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Button from "components/shared/Button/Button";
import { GlobalContext } from "context/Provider";
import API from "api";
import Session from "helpers/session";
import {
  associateReadingType,
} from "helpers/variables";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import { useTranslation } from "react-i18next";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  getYAxisRange,
  ChartProps,
  GraphInterface,
  Thresholds,
  BPThresholds,
  SpirThresholds,
  heartRate,
  formatChart,
  formatSeries,
  getXAxisConfig,
  getRange,
  shiftRange,
  getThresholdPlotBands,
  normalizeThresholds,
  Reading
} from "./Chart.utility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IconButton, RangeNavigation, ChartContainer } from "./Chart.styles";
import {
  HiddenFieldsChips,
  useSpirometryVisibility,
  spirometryLabels,
  defaultSpirometryVisibility
} from "views/PatientBoard/microViews/History/Measurements/Measurements.utils";

export default function Chart({type, memberId}: any) {
  const [thresholds, setThresholds] = useState<Thresholds | undefined>(undefined);
  const [bpThresholds, setBpThresholds] =
    useState<{ systolic: Thresholds; diastolic: Thresholds } | undefined>(undefined);
  const [spirThresholds, setSpirThresholds] =
    useState<{ FEV1: Thresholds; FVC: Thresholds; FEV1_FVC: Thresholds; PEF: Thresholds } | undefined>(undefined);
  const [range, setRange] = useState<"day" | "week" | "month" | "year">("month");
  const [referenceDate, setReferenceDate] = useState<Date>(new Date());
  const [graphData, setGraphData] = useState<GraphInterface[]>([]);
  const [stepsChartType, setStepsChartType] = useState<"line" | "column">("column");
  const { member } = useContext(GlobalContext);
  const { t } = useTranslation();
  const heartRateToggle = type !== heartRate.display;
  const { start, end } = getRange(range, referenceDate);
  const { visibility, hiddenFields, updateVisibility } = useSpirometryVisibility();

  const endPlusOne = end.clone().add(1, "day");
  const startUtc = start.clone().utc().format("YYYY-MM-DDTHH:mm:ss");
  const endUtc = endPlusOne.clone().utc().format("YYYY-MM-DDTHH:mm:ss");
  const isStepsDay = type === "activity" && range === "day";
  const isSleepDay = type === "Sleep" && range === "day";
  const isNestedDay = isStepsDay || isSleepDay;
  const isColumns = isNestedDay && stepsChartType === "column";

  const chartRender = async () => {
    if (!member?.current?.id) return;

    let dataForChart;

    const additionalSources = heartRateToggle ? "" : "&type=spo2&type=bp";

    try {
      const [readings, memberThresholds, defaultThresholds] = await Promise.all([
        API.kore.readings.getForOne(
          member.current.id,
          Session.crackCookieToken("coordinator"),
          associateReadingType(type),
          `${additionalSources}measured_between=${startUtc},${endUtc}`
        ),
        API.kore.readings.getThreshold(
          member.current.id || "default",
          associateReadingType(type),
          Session.crackCookieToken("coordinator")
        ),
        API.kore.readings.getThreshold(
          "default",
          associateReadingType(type),
          Session.crackCookieToken("coordinator")
        ),
      ]);

      const filteredReadings = readings.items.filter((reading: Reading) => {
        const ts = new Date(reading.measuredAt).getTime();
        return ts >= start.valueOf() && ts <= end.valueOf();
      });

      if (isNestedDay) {
        const batches = readings.items.flatMap((entry: any) =>
          (entry.metrics?.readings ?? []).map((r: any) => ({
            ...r,
            measuredAt:
              String(r.measuredAt).replace(/([Zz]|[+-]\d\d:?\d\d)$/, "") + "Z",
          }))
        );

        dataForChart = batches.filter((b: any) => {
          const ts = new Date(b.measuredAt).getTime();
          return ts >= start.valueOf() && ts <= end.valueOf();
        });
      } else {
        dataForChart = filteredReadings;
      }

      setGraphData(formatChart(dataForChart, type));

      if (type === "Blood Pressure") {
        const normDefault = normalizeThresholds(defaultThresholds, type) as BPThresholds;
        const normMember  = normalizeThresholds(memberThresholds, type)  as BPThresholds;

        const mergedSystolic: Thresholds = {
          ...(normDefault?.systolic ?? {}),
          ...(normMember?.systolic ?? {})
        };

        const mergedDiastolic: Thresholds = {
          ...(normDefault?.diastolic ?? {}),
          ...(normMember?.diastolic ?? {})
        };

        setBpThresholds({ systolic: mergedSystolic, diastolic: mergedDiastolic });
      } else if (type === "Spirometry") {
        const normDefault = normalizeThresholds(defaultThresholds, type) as SpirThresholds;
        const normMember  = normalizeThresholds(memberThresholds, type)  as SpirThresholds;

        const mergedFEV1: Thresholds = {
          ...(normDefault?.FEV1 ?? {}),
          ...(normMember?.FEV1 ?? {})
        };

        const mergedFVC: Thresholds = {
          ...(normDefault?.FVC ?? {}),
          ...(normMember?.FVC ?? {})
        };

        const mergedFEV1_FVC: Thresholds = {
          ...(normDefault?.FEV1_FVC ?? {}),
          ...(normMember?.FEV1_FVC ??{})
        };

        const mergedPEF: Thresholds = {
          ...(normDefault?.PEF ?? {}),
          ...(normMember?.PEF ?? {})
        };

        setSpirThresholds({ FEV1: mergedFEV1, FVC: mergedFVC, FEV1_FVC: mergedFEV1_FVC, PEF: mergedPEF });
      } else {
        let merged: any;

        const normalizedDefaultThresholds = normalizeThresholds(defaultThresholds, type);
        const normalizedMemberThresholds  = normalizeThresholds(memberThresholds, type);

        merged = {
          ...(normalizedDefaultThresholds as Thresholds),
          ...(normalizedMemberThresholds as Thresholds)
        };

        setThresholds(merged);
      }

    } catch (err) { }
  };

  useEffect(() => {
    chartRender();
  }, [type, range, referenceDate, heartRateToggle, member?.current?.id]);

  const yAxisRange = getYAxisRange(type, graphData.length > 0);

  Highcharts.setOptions({
    time: {
      timezoneOffset: new Date().getTimezoneOffset(),
    }
  });

  const buildOptions = (
    seriesData: any[],
    thresholds: Thresholds | undefined,
    fieldOption?: "systolic" | "diastolic" | "FEV1" | "FVC" | "FEV1_FVC" | "PEF",
  ) => ({
    chart: {
      type: isColumns ? "column" : "line",
      plotBorderColor: 'black',
      plotBorderWidth: 1.25,
    },
    ...(isColumns
      ? {
        plotOptions: {
          column: { pointRange: 3600 * 1000, borderRadius: 2, pointPadding: 0.1, groupPadding: 0.05 },
        },
      }
      : {}),
    title: {
      text: isStepsDay
        ? `Total: ${graphData.reduce((s, d) => s + (d.activity ?? 0), 0)} steps`
        : isSleepDay
          ? `Total: ${graphData.reduce((s, d) => s + (d.sleep ?? 0), 0).toFixed(2)} hrs`
          : null,
    },
    xAxis: {
      type: "datetime",
      gridLineDashStyle: "Dash",
      ...getXAxisConfig(range, start.valueOf(), end.valueOf()),
    },
    yAxis: {
      title: { text: null },
      minPadding: 0.05,
      maxPadding: 0.05,
      gridLineColor: "black",
      gridLineWidth: 1.25,
      ...(isNestedDay
        ? { min: 0, max: yAxisRange?.max, startOnTick: true, endOnTick: true, plotBands: [] }
        : {
          min: yAxisRange?.min,
          max: yAxisRange?.max,
          startOnTick: false,
          endOnTick: false,
          plotBands: getThresholdPlotBands(
            thresholds,
            yAxisRange?.min ?? (thresholds?.lowEmergency ?? 0) - 5,
            yAxisRange?.max ?? (thresholds?.highEmergency ?? 100) + 5
          ),
        }),
    },
    legend: { enabled: true },
    tooltip: {
      shared: true,
      xDateFormat: "%b %e, %Y %H:%M",
    },
    series: formatSeries(type, graphData, isStepsDay ? undefined : thresholds, fieldOption),
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          overflow: "auto",
          position: "fixed",
          top: 250,
          right: 110,
          bottom: 68,
          left: 290,
          paddingBottom: 10
        }}
      >
        <EncounterTimerDisplay/>
        {type === "Spirometry" && (
          <HiddenFieldsChips
            hiddenFields={hiddenFields}
            labels={spirometryLabels}
            sx={{ pl: 10 }}
            onRestore={(field) =>
              updateVisibility({
                ...visibility,
                [field]: true,
              })
            }
          />
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <ButtonGroup>
            {["day", "week", "month", "year"].map(mode => (
              <Button
                key={mode}
                variant="info"
                margin={1}
                selected={range === mode}
                onClick={() => setRange(mode as any)}
              >
                {mode}
              </Button>
            ))}
          </ButtonGroup>
          <RangeNavigation>
            <IconButton
              onClick={() => setReferenceDate(previousRange => shiftRange(range, previousRange, -1))}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography sx={{ paddingLeft: 1.5, paddingRight: 1.5}}>
              {range === "day"
                ? getRange(range, referenceDate).start.format("MMM D, YYYY")
                : `${getRange(range, referenceDate).start.format("MMM D, YYYY")}
                  –
                  ${getRange(range, referenceDate).end.format("MMM D, YYYY")}`
              }
            </Typography>
            <IconButton
              onClick={() => setReferenceDate(previousRange => shiftRange(range, previousRange, 1))}
            >
              <ChevronRightIcon />
            </IconButton>
          </RangeNavigation>
          {isNestedDay && (
            <ButtonGroup>
              <Button
                variant="info"
                selected={stepsChartType === "column"}
                onClick={() => setStepsChartType("column")}
              >
                Bar
              </Button>
              <Button
                variant="info"
                selected={stepsChartType === "line"}
                onClick={() => setStepsChartType("line")}
              >
                Line
              </Button>
            </ButtonGroup>
          )}
        </Stack>
        {type === "Blood Pressure" ? (
          <>
            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={buildOptions(
                  graphData,
                  bpThresholds?.systolic,
                  "systolic"
                )}
              />
            </ChartContainer>
            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={buildOptions(
                  graphData,
                  bpThresholds?.diastolic,
                  "diastolic"
                )}
              />
            </ChartContainer>
          </>
        ) : type === "Spirometry" ? (
          <>
            {visibility.fev1 && (
              <ChartContainer>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={buildOptions(
                    graphData,
                    spirThresholds?.FEV1,
                    "FEV1"
                  )}
                />
              </ChartContainer>
            )}
            {visibility.fvc && (
              <ChartContainer>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={buildOptions(
                    graphData,
                    spirThresholds?.FVC,
                    "FVC"
                  )}
                />
              </ChartContainer>
            )}
            {visibility.fev1fvc && (
              <ChartContainer>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={buildOptions(
                    graphData,
                    spirThresholds?.FEV1_FVC,
                    "FEV1_FVC"
                  )}
                />
              </ChartContainer>
            )}
            {visibility.pef && (
              <ChartContainer>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={buildOptions(
                    graphData,
                    spirThresholds?.PEF,
                    "PEF"
                  )}
                />
              </ChartContainer>
            )}
          </>
        ) : (
          <ChartContainer>
            <HighchartsReact
              highcharts={Highcharts}
              options={buildOptions(
                graphData,
                thresholds
              )}
            />
          </ChartContainer>
        )}
      </Box>
    </LocalizationProvider>
  );
}
